import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useFollowingForUser } from './queries/usePeopleQueries';
import { invalidateAfterFollow } from '../lib/query/invalidateMutations';
import { NOTIFICATION_TYPES } from '../utils/notifications';

/**
 * Follow back from a notification row or the notification toast: who you already follow, and a follow that
 * writes the same row + "New follower" notification Profile's Follow button does. Optimistic per id.
 */
export default function useFollowBack() {
  const queryClient = useQueryClient();
  const { session, profile } = useCurrentUserProfile();
  const viewerId = session?.user?.id ?? null;
  const { data: following = [] } = useFollowingForUser(viewerId, { limit: 1000 });
  const [pending, setPending] = useState(() => new Set());
  const followingIds = useMemo(() => new Set(following.map((p) => p.id)), [following]);

  const isFollowing = useCallback(
    (userId) => followingIds.has(userId) || pending.has(userId),
    [followingIds, pending],
  );

  const followBack = useCallback(
    async (userId) => {
      if (!viewerId || !userId || userId === viewerId || followingIds.has(userId)) return;
      setPending((prev) => new Set(prev).add(userId));
      try {
        const { error } = await supabase
          .from('follows')
          .insert({ follower_id: viewerId, following_id: userId });
        if (error) throw error;
        const name = profile?.display_name || session?.user?.email?.split('@')[0] || 'Someone';
        await supabase.from('notifications').insert({
          user_id: userId,
          type: NOTIFICATION_TYPES.FOLLOW,
          title: 'New follower',
          message: `${name} started following you`,
          related_user_id: viewerId,
          read: false,
          created_at: new Date().toISOString(),
        });
        invalidateAfterFollow(queryClient, { viewerId, targetUserId: userId });
      } catch (err) {
        console.error('[useFollowBack] follow failed:', err);
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    },
    [viewerId, followingIds, profile?.display_name, session?.user?.email, queryClient],
  );

  return { isFollowing, followBack };
}
